import { IGetResBuildingUnit, IGetResReadReceiptSummary, TSFixMe } from '../frontend-libs/evlapp-types';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { createFilter } from 'react-select';
import cx from 'classnames';
import {
  Button,
  Card,
  Content,
  FileField,
  FormMessage,
  Header,
  Icon,
  Modal,
  RadioField,
  ReadReceiptBadge,
  SingleSelectField,
  Text,
} from '../common/components';
import { useModal } from '../common/components/Modal/useModal';
import routes from '../common/constants/routes';

import { useAppSelector, useDocumentTitle } from '../common/hooks';
import { t } from '../common/i18n';
import { selectSecurity } from '../common/store/securitySlice';
import { setFlashMessage } from '../common/store/uiSlice';
import { getReadReceiptSummary } from '../common/services/ReadReceiptService';
import { getProfileName } from '../common/utils/Profile/profile';
import { formatDateMonthYear } from '../common/utils/Date/Date';
import Yup from '../common/utils/FormValidation';
import { getUnits } from '../Units/services/UnitService';
import { ModalDelete } from './components/ModalDelete';
import { ModalRename } from './components/ModalRename';
import { BUILDING_FILE_CATEGORIES, UNIT_FILE_CATEGORIES } from './config/fileCategories';
import { downloadStorageFile, getFiles, getMyUnitFiles, IFile, uploadFiles } from './services/FilesService';
import { sortByYearMonth } from './utils/FilesUtils';

export const FilesDefault: React.FC = () => {
  const namespace = 'Files';
  useDocumentTitle(t('title', namespace));

  const ui = useAppSelector(selectSecurity).ui;
  const isAdmin = ui.state === 'admin' ? true : undefined;
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [formError, setFormError] = useState(false);

  const [files, setFiles] = useState<IFile[]>([]);
  const [myUnitFiles, setMyUnitFiles] = useState<IFile[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [units, setUnits] = useState<IGetResBuildingUnit | null>(null);
  const [receiptSummary, setReceiptSummary] = useState<IGetResReadReceiptSummary | null>(null);
  const [activeFile, setActiveFile] = useState<TSFixMe>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isShownUpload, toggleUpload] = useModal();
  const [isShownRename, toggleRename] = useModal();
  const [isShownDelete, toggleDelete] = useModal();

  const fetchFiles = async () => {
    const files = await getFiles();
    setFiles(files);
    setIsLoading(false);
  };

  const fetchMyUnitFiles = async () => {
    try {
      const files = await getMyUnitFiles();
      setMyUnitFiles(files.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    } catch {
      // starší API endpoint nezná — sekce se prostě nezobrazí
      setMyUnitFiles([]);
    }
  };

  useEffect(() => {
    fetchFiles();
    fetchMyUnitFiles();
    if (isAdmin) {
      getReadReceiptSummary('building-file')
        .then((res) => setReceiptSummary(res))
        .catch(() => undefined);
    }
  }, [isAdmin]);

  // Jednotky pro upload "konkrétní jednotce" — načítá se líně při prvním otevření modalu (admin-only endpoint)
  useEffect(() => {
    if (isShownUpload && isAdmin && units === null) {
      getUnits()
        .then((res) => setUnits(res))
        .catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShownUpload, isAdmin]);

  // Filtr má smysl jen pro kategorie, které v úložišti reálně jsou
  const presentCategories = useMemo(() => {
    const used = new Set(files.map((file) => file.category));
    return BUILDING_FILE_CATEGORIES.filter((category) => used.has(category));
  }, [files]);

  const visibleFiles = useMemo(
    () => (categoryFilter === 'all' ? files : files.filter((file) => file.category === categoryFilter)),
    [files, categoryFilter]
  );

  const groupedFiles: TSFixMe = useMemo(() => sortByYearMonth(visibleFiles), [visibleFiles]);

  const formik = useFormik<TSFixMe>({
    initialValues: { attachment: null, scope: 'building', unitId: '', category: 'other' },
    validate: () => setFormError(false),
    validationSchema: Yup.object({
      attachment: Yup.string().nullable().required(),
      unitId: Yup.string().when('scope', {
        is: 'unit',
        then: Yup.string().required(t('ModalUpload.Unit.required', namespace)),
        otherwise: Yup.string(),
      }),
    }),
    onSubmit: (values) => {
      const formData = new FormData();

      for (let i = 0; i < values.attachment.length; i++) {
        formData.append(`files[${i}]`, values.attachment[i]);
      }
      formData.append('category', values.category);
      if (values.scope === 'unit') {
        formData.append('unitId', values.unitId);
      }

      const unitLabel = units?.find((unit) => unit.id === values.unitId)?.label ?? '';

      uploadFiles(formData)
        .then(() => {
          // TODO: REFACTOR INTO REDUX
          fetchFiles().then(() => {
            formik.setSubmitting(false);
            formik.resetForm();
            if (fileInputRef.current) fileInputRef.current.value = '';
            dispatch(
              setFlashMessage({
                message:
                  values.scope === 'unit'
                    ? t('ModalUpload.Flash.successUnit', namespace, { unit: unitLabel })
                    : t('ModalUpload.Flash.success', namespace),
                type: 'success',
              })
            );
            toggleUpload();
          });
        })
        .catch(() => {
          setFormError(true);
          formik.setSubmitting(false);
        });
    },
  });

  const handleDelete = (item: TSFixMe) => {
    setActiveFile(item);
    toggleDelete();
  };

  const handleRenameFile = (item: TSFixMe) => {
    setActiveFile(item);
    toggleRename();
  };

  const unitOptions = useMemo(
    () =>
      (units ?? []).map((unit) => ({
        value: unit.id,
        label: `č. ${unit.label}${
          unit.owners?.length
            ? ` — ${unit.owners.map((owner: TSFixMe) => getProfileName(owner.profile)).join(', ')}`
            : ''
        }`,
      })),
    [units]
  );
  const unitValue = unitOptions.find((option) => option.value === formik.values.unitId) ?? null;

  const categoryOptions = useMemo(() => {
    const values = formik.values.scope === 'unit' ? UNIT_FILE_CATEGORIES : BUILDING_FILE_CATEGORIES;
    return values.map((category) => ({ value: category, label: t(`Category.${category}`, namespace) }));
  }, [formik.values.scope]);
  const categoryValue = categoryOptions.find((option) => option.value === formik.values.category) ?? null;

  // Soubory vlastníkovy jednotky seskupené po jednotkách (může vlastnit víc jednotek)
  const myUnitGroups = useMemo(() => {
    const groups: { unitId: string; unitLabel: string; files: IFile[] }[] = [];
    myUnitFiles.forEach((file) => {
      let group = groups.find((item) => item.unitId === file.unitId);
      if (!group) {
        group = { unitId: file.unitId ?? '', unitLabel: file.unitLabel ?? '', files: [] };
        groups.push(group);
      }
      group.files.push(file);
    });
    return groups;
  }, [myUnitFiles]);

  const handleScopeChange = (scope: string) => {
    formik.setFieldValue('scope', scope);
    // kategorie jsou per-scope — při přepnutí vrátit bezpečný default
    formik.setFieldValue('category', 'other');
    if (scope === 'building') formik.setFieldValue('unitId', '');
  };

  return (
    <>
      <Header
        title={t('Header.title', namespace)}
        customBacklink={routes.poll}
        actions={
          !isLoading && isAdmin && <Button size="small" title={t(`Header.upload`, namespace)} onClick={toggleUpload} />
        }
      />
      <Content
        isLoading={isLoading}
        isEmpty={files.length === 0 && myUnitFiles.length === 0}
        isEmptyText={t('Content.emptyText', namespace)}
      >
        {myUnitFiles.length > 0 && (
          <>
            <Text size="h2" color="Neutral800" className="mb-2">
              {t('MyUnit.title', namespace)}
            </Text>
            {myUnitGroups.map((group) => (
              <div key={group.unitId} className="mb-3">
                {myUnitGroups.length > 1 && (
                  <Text as="div" size="body-small" color="Neutral600" className="mb-1">
                    {t('MyUnit.unit', namespace, { label: group.unitLabel })}
                  </Text>
                )}
                <Card className="CardAttachments">
                  {group.files.map((item, index) => (
                    <React.Fragment key={item.id}>
                      <div className="Card__link">
                        <div className="mr-auto">
                          <Text size="body-medium" color="Inherit">
                            {item.filename}
                          </Text>
                          {!!item.category && item.category !== 'other' && (
                            <Text as="div" size="body-small" color="Neutral600">
                              {t(`Category.${item.category}`, namespace)}
                            </Text>
                          )}
                        </div>

                        <Icon
                          name="download"
                          color="Inherit"
                          className="icon--clickable"
                          onClick={() => downloadStorageFile(item)}
                        />
                      </div>
                      {index !== group.files.length - 1 && <div className="Card__divider"></div>}
                    </React.Fragment>
                  ))}
                </Card>
              </div>
            ))}

            <Text size="h2" color="Neutral800" className="mt-4 mb-2">
              {t('Shared.title', namespace)}
            </Text>
          </>
        )}

        {presentCategories.length > 1 && (
          <div className="FileFilter">
            <button
              type="button"
              className={cx('FileFilter__item', categoryFilter === 'all' && 'FileFilter__item--active')}
              onClick={() => setCategoryFilter('all')}
            >
              {t('Filter.all', namespace)}
            </button>
            {presentCategories.map((category) => (
              <button
                key={category}
                type="button"
                className={cx('FileFilter__item', categoryFilter === category && 'FileFilter__item--active')}
                onClick={() => setCategoryFilter(category)}
              >
                {t(`Category.${category}`, namespace)}
              </button>
            ))}
          </div>
        )}

        {Object.keys(groupedFiles)
          .sort()
          .reverse()
          .map((key: TSFixMe) => {
            return (
              <div key={key} className="mb-4">
                <Text size="h2" className="mb-1">
                  {formatDateMonthYear(key)}
                </Text>
                <Card className="CardAttachments">
                  {groupedFiles[key].map((item: IFile, index: number) => {
                    return (
                      <React.Fragment key={item.id}>
                        <div className="Card__link">
                          <div className="mr-auto">
                            <Text size="body-medium" color="Inherit">
                              {item.filename}
                            </Text>
                            {!!item.category && item.category !== 'other' && (
                              <Text as="div" size="body-small" color="Neutral600">
                                {t(`Category.${item.category}`, namespace)}
                              </Text>
                            )}
                          </div>

                          {isAdmin && receiptSummary !== null && (
                            <ReadReceiptBadge
                              subjectType="building-file"
                              subjectId={item.id}
                              className="mr-2"
                              preloaded={{
                                readCount:
                                  receiptSummary.subjects.find((subject) => subject.subjectId === item.id)?.readCount ??
                                  0,
                                totalCount: receiptSummary.totalMembers,
                              }}
                            />
                          )}

                          <Icon
                            name="download"
                            color="Inherit"
                            className="icon--clickable"
                            onClick={() => downloadStorageFile(item)}
                          />

                          {isAdmin && (
                            <div className="CardActions">
                              <Icon name="edit" onClick={() => handleRenameFile(item)} />
                              <Icon name="trash" onClick={() => handleDelete(item)} />
                            </div>
                          )}
                        </div>
                        {index !== groupedFiles[key].length - 1 && <div className="Card__divider"></div>}
                      </React.Fragment>
                    );
                  })}
                </Card>
              </div>
            );
          })}
      </Content>

      <Modal isShown={isShownUpload} onHide={toggleUpload}>
        <Text size="h2" align="center" className="mb-2">
          {t('ModalUpload.title', namespace)}
        </Text>
        <Text size="body-medium" color="Neutral700" align="center" className="mb-3">
          {t('ModalUpload.subtitle', namespace)}
        </Text>
        {formError && <FormMessage message={t('ModalUpload.formError', namespace)} variant="dark" />}

        <RadioField
          name="scope"
          value="building"
          className="mb-1"
          label={t('ModalUpload.Scope.building', namespace)}
          description={t('ModalUpload.Scope.building_description', namespace)}
          onChange={(e) => handleScopeChange(e.target.value)}
          checked={formik.values.scope === 'building'}
        />
        <RadioField
          name="scope"
          value="unit"
          className="mb-2"
          label={t('ModalUpload.Scope.unit', namespace)}
          description={t('ModalUpload.Scope.unit_description', namespace)}
          onChange={(e) => handleScopeChange(e.target.value)}
          checked={formik.values.scope === 'unit'}
        />

        {formik.values.scope === 'unit' && (
          <SingleSelectField
            label={t('ModalUpload.Unit.label', namespace)}
            placeholder={t('ModalUpload.Unit.placeholder', namespace)}
            name="unitId"
            value={unitValue}
            onChange={(option) => formik.setFieldValue('unitId', (option as { value: string } | null)?.value ?? '')}
            onBlur={() => formik.setFieldTouched('unitId', true)}
            options={unitOptions}
            // default filtr react-selectu hledá i ve value (UUID jednotky obsahuje číslice) — hledat jen v labelu
            filterOption={createFilter({ stringify: (option) => String(option.label) })}
            isLoading={units === null}
            error={{ message: formik.errors.unitId as string, touched: formik.touched.unitId as boolean }}
            className="mb-2"
          />
        )}

        <SingleSelectField
          label={t('ModalUpload.Category.label', namespace)}
          name="category"
          value={categoryValue}
          onChange={(option) =>
            formik.setFieldValue('category', (option as { value: string } | null)?.value ?? 'other')
          }
          options={categoryOptions}
          className="mb-2"
        />

        <FileField
          name="attachment"
          label={t('ModalUpload.Attachment.label', namespace)}
          filesLabel={`${namespace}.ModalUpload.Attachment.filesLabel`}
          multiple={true}
          reference={fileInputRef}
          formikValue={formik.values.attachment}
          className="mt-2 mb-4"
          error={{ message: formik.errors.attachment as string, touched: formik.touched.attachment as boolean }}
          onChange={(event) => {
            if (event && event.currentTarget && event.currentTarget.files) {
              formik.setFieldValue('attachment', event.currentTarget.files);
            }
          }}
        />

        <div className="Flex FlexAlign--center FlexJustify--center mb-2">
          <Button
            onClick={formik.handleSubmit}
            title={t('ModalUpload.submit', namespace)}
            loading={formik.isSubmitting}
            disabledDeps={['demo', 'expired']}
            wide={true}
          />
        </div>
      </Modal>
      <ModalDelete
        id={activeFile?.id || ''}
        isShown={isShownDelete}
        onHide={toggleDelete}
        handleFetchFiles={fetchFiles}
      />
      <ModalRename file={activeFile} isShown={isShownRename} onHide={toggleRename} handleFetchFiles={fetchFiles} />
    </>
  );
};
